import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseMutationReturnType,
  type UseQueryOptions,
  type UseQueryReturnType,
} from '@tanstack/vue-query'
import { ApiException } from '@/api/types'
import { useToastStore } from '@/stores/toast'

/**
 * Streama CMS · Vue Query wrappers
 *
 * Thin layer over TanStack Vue Query. Defaults tuned for an admin
 * console:
 *   - staleTime: 30s — admin lists don't need to refetch on every
 *                       focus; 30 seconds keeps things responsive
 *                       without hammering the backend
 *   - retry: 1     — fail fast on transient errors; the admin can
 *                       retry manually
 *   - errors throw ApiException — the axios interceptor already
 *                       converted them
 *
 * For mutations, the wrapper toasts errors automatically unless
 * the caller opts out via { silentErrors: true }. v5 moved the
 * onError option from useMutation's options object onto a separate
 * lifecycle pattern; we adapt by intercepting via the
 * mutationFn wrapper (see useApiMutation).
 */

/**
 * Read query. Sugar over useQuery with sane admin defaults.
 *
 * @example
 *   const { data, isLoading, error } = useApiQuery({
 *     queryKey: ['subscribers', { username }],
 *     queryFn: () => subscriberApi.search(username),
 *   })
 */
export function useApiQuery<TData, TError = ApiException>(
  options: UseQueryOptions<TData, TError>,
): UseQueryReturnType<TData, TError> {
  return useQuery<TData, TError>({
    staleTime: 30_000,
    retry: 1,
    refetchOnWindowFocus: true,
    ...options,
  })
}

/**
 * Write mutation. Sugar over useMutation. Default behaviour:
 *   - On error: toast the API exception's message
 *   - On success: caller decides via the standard onSuccess pattern
 *     (passed inline at mutate() call sites, not on the options
 *     object — TanStack Vue Query v5 convention).
 *
 * Pass `silentErrors: true` to suppress the auto-toast when the
 * caller wants custom error UI (e.g. inline form validation).
 *
 * The error toast is wired by wrapping the supplied mutationFn so
 * exceptions surface through a single bottleneck — this avoids the
 * v5 type changes around onError in the options object while
 * preserving the v4-era ergonomics for callers.
 */
export interface UseApiMutationOptions<TData, TError, TVariables>
  extends Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'> {
  mutationFn: (variables: TVariables) => Promise<TData>
  silentErrors?: boolean
  onSuccess?: (data: TData, variables: TVariables, context: unknown) => void | Promise<void>
  onError?: (error: TError, variables: TVariables, context: unknown) => void | Promise<void>
}

export function useApiMutation<TData, TVariables, TError = ApiException>(
  options: UseApiMutationOptions<TData, TError, TVariables>,
): UseMutationReturnType<TData, TError, TVariables, unknown> {
  const { silentErrors, mutationFn, ...rest } = options
  const toast = useToastStore()

  const wrappedMutationFn: typeof mutationFn = async (vars: TVariables) => {
    try {
      return await mutationFn(vars)
    } catch (err) {
      if (!silentErrors && err instanceof ApiException) {
        toast.push({
          kind: 'error',
          message: err.message,
          detail: err.code !== 'UNKNOWN' ? err.code : undefined,
        })
      }
      throw err
    }
  }

  return useMutation<TData, TError, TVariables>({
    ...rest,
    mutationFn: wrappedMutationFn,
  })
}
